import { Button } from "@/shared/ui/button";
import { ArrowRight, Scan, Brain, Zap, Shield } from "lucide-react";
import { useState } from "react";
import { SignupModal } from "./SignupModal";

export function LandingHero() {
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero pt-16">
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(255,255,255) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      {/* Floating Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Next-Gen Asset Intelligence</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">ROBOTIC</span>
              <br />
              IMAGING
              <br />
              <span className="text-muted-foreground text-3xl md:text-4xl lg:text-5xl">Asset Management</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl leading-relaxed">
              Revolutionary AI-powered platform that transforms facility management through 
              robotic imaging, predictive analytics, and automated workflows.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button variant="default" size="lg" className="group" onClick={() => setIsSignupOpen(true)}>
                Start Engineering Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button variant="outline" size="lg">
                View Technical Demo
              </Button>
            </div>

            {/* Engineering Benefits */}
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Scan className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">OCR Integration</div>
                  <div className="text-sm text-muted-foreground">Instant data capture</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">AI Diagnostics</div>
                  <div className="text-sm text-muted-foreground">Predictive maintenance</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">Automation</div>
                  <div className="text-sm text-muted-foreground">Workflow optimization</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="bg-primary/20 rounded-lg p-2">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">Compliance</div>
                  <div className="text-sm text-muted-foreground">Audit ready</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="relative animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="relative bg-gradient-accent rounded-2xl p-8 border border-primary/20">
              {/* Dashboard Preview */}
              <div className="bg-card rounded-lg p-6 shadow-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-lg font-semibold text-foreground">Asset Overview</div>
                  <div className="bg-primary/20 rounded-lg px-3 py-1">
                    <span className="text-primary text-sm font-medium">Live Data</span>
                  </div>
                </div>
                
                {/* Mock Data Visualization */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">HVAC Systems</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-accent rounded-full">
                        <div className="w-20 h-2 bg-primary rounded-full"></div>
                      </div>
                      <span className="text-foreground font-medium">87%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Power Distribution</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-accent rounded-full">
                        <div className="w-22 h-2 bg-primary rounded-full"></div>
                      </div>
                      <span className="text-foreground font-medium">94%</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Safety Systems</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-accent rounded-full">
                        <div className="w-24 h-2 bg-primary rounded-full"></div>
                      </div>
                      <span className="text-foreground font-medium">100%</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Status Cards */}
              <div className="absolute -top-4 -right-4 bg-primary rounded-lg p-3 shadow-glow">
                <div className="text-primary-foreground text-sm font-medium">99.2% Uptime</div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-card border border-primary/20 rounded-lg p-3 shadow-card">
                <div className="text-foreground text-sm font-medium">$2.3M Saved</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <SignupModal isOpen={isSignupOpen} onClose={() => setIsSignupOpen(false)} />
    </section>
  );
}